<template>
  <el-dialog
    :title="'个人周看板'"
    :visible.sync="dialogVisible"
    append-to-body
    :close-on-click-modal="false"
    width="1180px"
    custom-class="week-board-dialog"
    @close="handleClose"
  >
    <!-- 头部：工程师信息 + 周历翻页 -->
    <div class="week-board__header">
      <div class="week-board__engineer">
        <i class="el-icon-user-solid"></i>
        <span class="week-board__engineer-name">{{ engineerName || '--' }}</span>
      </div>
      <div class="week-board__nav">
        <el-button size="mini" icon="el-icon-arrow-left" :disabled="loading" @click="handlePrevWeek">上一周</el-button>
        <span class="week-board__range">{{ weekRangeLabel }}</span>
        <el-button size="mini" :disabled="loading" @click="handleNextWeek">下一周<i class="el-icon-arrow-right el-icon--right"></i></el-button>
        <el-button size="mini" type="text" :disabled="loading" @click="handleThisWeek">回本周</el-button>
      </div>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="week-board__loading">
      <i class="el-icon-loading"></i>
      <span>正在加载周看板…</span>
    </div>

    <!-- 7 日列 -->
    <div v-else class="week-board__grid">
      <div
        v-for="day in days"
        :key="day.date"
        class="day-col"
        :class="{ 'day-col--today': isToday(day.date) }"
      >
        <!-- 日头 -->
        <div class="day-col__head">
          <div class="day-col__date">
            <span class="day-col__weekday">{{ formatWeekday(day.date) }}</span>
            <span class="day-col__day">{{ formatDayLabel(day.date) }}</span>
            <span v-if="isToday(day.date)" class="day-col__today-tag">今天</span>
          </div>
          <div class="day-col__status">
            <span
              class="status-chip"
              :class="day.dingTalkScheduleStatus === '休假' ? 'status-chip--rest' : 'status-chip--work'"
            >排班·{{ day.dingTalkScheduleStatus || '—' }}</span>
            <span
              class="status-chip"
              :class="attendanceChipClass(day.attendanceStatus)"
            >考勤·{{ day.attendanceStatus || '—' }}</span>
          </div>
        </div>

        <!-- 工单卡片 -->
        <div class="day-col__body">
          <template v-if="day.items && day.items.length">
            <div
              v-for="(card, cardIndex) in visibleItems(day)"
              :key="card.orderId + '-' + cardIndex"
              class="stage-card"
              :class="cardClass(card)"
            >
              <!-- 卡头：阶段名 + 单类型 -->
              <div class="stage-card__head">
                <span class="stage-card__name" :title="card.stageName || card.orderType">
                  {{ card.stageName || card.orderType || '派单' }}
                </span>
                <span class="stage-card__type" :class="card.orderType === '售后' ? 'stage-card__type--after' : ''">
                  {{ card.orderType || '派单' }}
                </span>
              </div>

              <!-- 预警角标区：延误预警 + 计划漂移，独立叠加 -->
              <div v-if="warningLabel(card) || driftLabel(card)" class="stage-card__badges">
                <span v-if="warningLabel(card)" class="warn-badge" :class="warningClass(card)">
                  {{ warningLabel(card) }}
                </span>
                <span v-if="driftLabel(card)" class="warn-badge warn-badge--drift">
                  {{ driftLabel(card) }}
                </span>
              </div>

              <!-- 地址 -->
              <div class="stage-card__addr" :title="card.address">
                <i class="el-icon-location-outline"></i>{{ card.address || '--' }}
              </div>

              <!-- 施工状态 -->
              <div class="stage-card__row">
                <span class="stage-card__status" :class="statusClass(card.constructionStatus)">
                  {{ card.constructionStatus || '--' }}
                </span>
              </div>

              <!-- 计划时间：防水走半天槽计划窗，售后走预约时间 -->
              <div class="stage-card__time" v-if="card.orderType === '售后'">
                <span class="stage-card__time-label">预约</span>
                <span>{{ card.reserveTime ? formatDateTime(card.reserveTime) : '--' }}</span>
              </div>
              <div class="stage-card__time" v-else>
                <span class="stage-card__time-label">计划</span>
                <span>{{ formatPlanWindow(card) }}</span>
              </div>

              <!-- 实际时间（与计划脱钩双展示） -->
              <div class="stage-card__time" v-if="card.actualStartTime || card.actualEndTime">
                <span class="stage-card__time-label">实际</span>
                <span>
                  {{ card.actualStartTime ? '入场 ' + formatDateTime(card.actualStartTime) : '' }}
                  {{ card.actualStartTime && card.actualEndTime ? ' / ' : '' }}
                  {{ card.actualEndTime ? '完成 ' + formatDateTime(card.actualEndTime) : '' }}
                </span>
              </div>
            </div>

            <!-- 折叠区：超过 3 张显示 +N，点开展示全部 -->
            <div
              v-if="collapsedCount(day) > 0"
              class="day-col__collapse"
              @click="toggleExpand(day.date)"
            >
              <template v-if="!expandedDays[day.date]">
                <i class="el-icon-arrow-down"></i> +{{ collapsedCount(day) }} 展开
              </template>
              <template v-else>
                <i class="el-icon-arrow-up"></i> 收起
              </template>
            </div>
          </template>

          <div v-else class="day-col__empty">
            <i class="el-icon-document"></i>
            <span>无工单</span>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">关 闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getEngineerWeekBoard } from "@/api/work/waterProof/merchants";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
// 同日堆叠排序权重：施工中 > 已接单 > 已完成
const STATUS_RANK = { "施工中": 0, "已接单": 1, "已完成": 2 };

export default {
  name: "engineerWeekBoard",

  data() {
    return {
      dialogVisible: false,
      loading: false,

      engineerUserId: "",
      engineerName: "",
      // 周一 0 点毫秒时间戳
      weekStart: 0,
      // 7 个日列
      days: [],
      // 按日期记录折叠展开状态
      expandedDays: {}
    };
  },

  computed: {
    /** 周范围文案，如 7月27日 ~ 8月2日 */
    weekRangeLabel() {
      if (!this.weekStart) return "";
      const start = new Date(this.weekStart);
      const end = new Date(this.weekStart + 6 * DAY_MS);
      return `${start.getMonth() + 1}月${start.getDate()}日 ~ ${end.getMonth() + 1}月${end.getDate()}日`;
    }
  },

  methods: {
    /**
     * 打开周看板
     * @param {Object} engineer - { engineerUserId, engineerName }
     */
    open(engineer) {
      this.engineerUserId = engineer.engineerUserId;
      this.engineerName = engineer.engineerName || "";
      this.weekStart = this.getMondayStart(Date.now());
      this.days = [];
      this.expandedDays = {};
      this.dialogVisible = true;
      this.loadBoard();
    },

    /** 计算指定时间所在周的周一 0 点毫秒时间戳 */
    getMondayStart(ts) {
      const d = new Date(ts);
      d.setHours(0, 0, 0, 0);
      // getDay：周日为 0，归一化为周一起点
      const weekday = d.getDay() || 7;
      d.setDate(d.getDate() - (weekday - 1));
      return d.getTime();
    },

    /** 请求周看板数据 */
    loadBoard() {
      if (!this.engineerUserId) {
        this.$message.error("工程师ID不能为空");
        return;
      }
      this.loading = true;
      this.expandedDays = {};
      // 接口为 GET + RequestBody，axios 通过 data 携带 body
      getEngineerWeekBoard({
        engineerUserId: this.engineerUserId,
        weekStart: this.weekStart
      })
        .then((res) => {
          if (!res || (res.code !== "200" && res.code !== 200)) {
            this.$message.error((res && res.message) || "获取周看板失败");
            this.days = [];
            return;
          }
          const board = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
          if (!board) {
            this.days = [];
            return;
          }
          if (board.engineerName) {
            this.engineerName = board.engineerName;
          }
          // 以服务端归一化后的周起点为准
          if (board.weekStart) {
            this.weekStart = board.weekStart;
          }
          const days = Array.isArray(board.days) ? board.days : [];
          // 服务端已排序，此处按文档规则兜底排一次
          this.days = days.map((day) => ({
            ...day,
            items: this.sortItems(day.items || [])
          }));
        })
        .catch((err) => {
          console.error("获取周看板失败:", err);
          this.$message.error("获取周看板失败");
          this.days = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /** 同日堆叠排序：施工中 > 已接单 > 已完成；同状态按 adjustedPlanEnd（售后取 reserveTime）升序 */
    sortItems(items) {
      return [...items].sort((a, b) => {
        const rankA = STATUS_RANK[a.constructionStatus] !== undefined ? STATUS_RANK[a.constructionStatus] : 9;
        const rankB = STATUS_RANK[b.constructionStatus] !== undefined ? STATUS_RANK[b.constructionStatus] : 9;
        if (rankA !== rankB) return rankA - rankB;
        const timeA = a.adjustedPlanEnd != null ? a.adjustedPlanEnd : (a.reserveTime != null ? a.reserveTime : Number.MAX_SAFE_INTEGER);
        const timeB = b.adjustedPlanEnd != null ? b.adjustedPlanEnd : (b.reserveTime != null ? b.reserveTime : Number.MAX_SAFE_INTEGER);
        return timeA - timeB;
      });
    },

    // ── 周历翻页 ──────────────────────────────────────────

    handlePrevWeek() {
      this.weekStart -= WEEK_MS;
      this.loadBoard();
    },

    handleNextWeek() {
      this.weekStart += WEEK_MS;
      this.loadBoard();
    },

    handleThisWeek() {
      this.weekStart = this.getMondayStart(Date.now());
      this.loadBoard();
    },

    // ── 折叠 ──────────────────────────────────────────────

    /** 折叠数量：优先取服务端 collapsedCount，兜底 max(0, items-3) */
    collapsedCount(day) {
      if (day.collapsedCount != null) return day.collapsedCount;
      const count = (day.items || []).length;
      return Math.max(0, count - 3);
    },

    /** 当日可见卡片：折叠时只展示前 3 张 */
    visibleItems(day) {
      const items = day.items || [];
      if (this.collapsedCount(day) > 0 && !this.expandedDays[day.date]) {
        return items.slice(0, 3);
      }
      return items;
    },

    toggleExpand(date) {
      this.$set(this.expandedDays, date, !this.expandedDays[date]);
    },

    // ── scheduleWarning / planDriftDays 渲染 ─────────────

    /** 延误预警文案（前端只做映射，不做时间计算） */
    warningLabel(card) {
      const days = this.formatDays(card.delayDays);
      // 没有具体天数（或为0）时不展示预警角标
      if (card.delayDays == null || Number(card.delayDays) <= 0) return "";
      switch (card.scheduleWarning) {
        case "OVERDUE":
          return `超期 ${days} 天`;
        case "DELAYED":
          return `延误 ${days} 天`;
        case "AHEAD":
          return `提前 ${days} 天`;
        default:
          return "";
      }
    },

    warningClass(card) {
      switch (card.scheduleWarning) {
        case "OVERDUE":
          return "warn-badge--overdue";
        case "DELAYED":
          return "warn-badge--delayed";
        case "AHEAD":
          return "warn-badge--ahead";
        default:
          return "";
      }
    },

    /** 计划漂移角标：planDriftDays > 0 时显示 */
    driftLabel(card) {
      if (card.planDriftDays != null && Number(card.planDriftDays) > 0) {
        return `计划顺延 ${this.formatDays(card.planDriftDays)} 天`;
      }
      return "";
    },

    /** 卡片边框状态色 */
    cardClass(card) {
      switch (card.scheduleWarning) {
        case "OVERDUE":
          return "stage-card--overdue";
        case "DELAYED":
          return "stage-card--delayed";
        case "AHEAD":
          return "stage-card--ahead";
        default:
          return "";
      }
    },

    /** 0.5 天粒度数字展示：1 → "1"，1.5 → "1.5" */
    formatDays(val) {
      if (val == null) return "";
      return String(Number(val));
    },

    // ── 状态样式 ──────────────────────────────────────────

    statusClass(status) {
      switch (status) {
        case "施工中":
          return "stage-card__status--working";
        case "已接单":
          return "stage-card__status--accepted";
        case "已完成":
          return "stage-card__status--done";
        default:
          return "";
      }
    },

    attendanceChipClass(status) {
      switch (status) {
        case "正常":
          return "status-chip--normal";
        case "迟到":
          return "status-chip--late";
        case "未打卡":
          return "status-chip--miss";
        default:
          return "status-chip--none";
      }
    },

    // ── 日期/时间格式化 ──────────────────────────────────

    /** 'yyyy-MM-dd' → Date（用 / 分隔避免 UTC 解析偏差） */
    parseDate(dateStr) {
      return new Date(String(dateStr).replace(/-/g, "/"));
    },

    isToday(dateStr) {
      const d = this.parseDate(dateStr);
      const now = new Date();
      return d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    },

    formatWeekday(dateStr) {
      const names = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
      return names[this.parseDate(dateStr).getDay()];
    },

    formatDayLabel(dateStr) {
      const d = this.parseDate(dateStr);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    },

    /** 半天槽渲染：时分 < 12:00 为上午，否则下午，如 "7月28日下午" */
    formatHalfSlot(ts) {
      if (ts == null) return "--";
      const d = new Date(Number(ts));
      const period = d.getHours() < 12 ? "上午" : "下午";
      return `${d.getMonth() + 1}月${d.getDate()}日${period}`;
    },

    /** 计划窗：adjustedPlanStart ~ adjustedPlanEnd（半天槽口径） */
    formatPlanWindow(card) {
      if (card.adjustedPlanStart == null && card.adjustedPlanEnd == null) return "--";
      return `${this.formatHalfSlot(card.adjustedPlanStart)} ~ ${this.formatHalfSlot(card.adjustedPlanEnd)}`;
    },

    /** 实际时间：M月d日 HH:mm */
    formatDateTime(ts) {
      if (ts == null) return "--";
      const d = new Date(Number(ts));
      const pad = (n) => (n < 10 ? "0" + n : String(n));
      return `${d.getMonth() + 1}月${d.getDate()}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    handleClose() {
      this.days = [];
      this.expandedDays = {};
    }
  }
};
</script>

<style lang="scss" scoped>
/* ── 头部 ─────────────────────────────────────────── */
.week-board__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.week-board__engineer {
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #409eff;
    font-size: 16px;
  }
}

.week-board__engineer-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.week-board__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.week-board__range {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  min-width: 130px;
  text-align: center;
}

/* ── 加载 ─────────────────────────────────────────── */
.week-board__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 80px 0;
  color: #409eff;
  font-size: 13px;

  i {
    font-size: 18px;
  }
}

/* ── 7 日列网格 ───────────────────────────────────── */
.week-board__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  align-items: start;
}

.day-col {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafbfc;
  overflow: hidden;
  min-height: 220px;
  display: flex;
  flex-direction: column;
}

.day-col--today {
  border-color: #409eff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.2);
}

.day-col__head {
  padding: 8px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.day-col--today .day-col__head {
  background: #ecf5ff;
}

.day-col__date {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.day-col__weekday {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.day-col__day {
  font-size: 12px;
  color: #909399;
}

.day-col__today-tag {
  margin-left: auto;
  font-size: 10px;
  color: #fff;
  background: #409eff;
  border-radius: 8px;
  padding: 0 6px;
  line-height: 16px;
}

.day-col__status {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* 排班/考勤状态胶囊 */
.status-chip {
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  white-space: nowrap;
}

.status-chip--work {
  color: #409eff;
  background: rgba(64, 158, 255, 0.12);
}

.status-chip--rest {
  color: #909399;
  background: #f0f2f5;
}

.status-chip--normal {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.12);
}

.status-chip--late {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.12);
}

.status-chip--miss {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.12);
}

.status-chip--none {
  color: #c0c4cc;
  background: #f5f7fa;
}

/* ── 日列主体 ─────────────────────────────────────── */
.day-col__body {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.day-col__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  padding: 20px 0;
  color: #c0c4cc;
  font-size: 12px;

  i {
    font-size: 22px;
  }
}

.day-col__collapse {
  text-align: center;
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
  padding: 4px 0;
  border-radius: 4px;

  &:hover {
    background: #ecf5ff;
  }
}

/* ── 工单卡片 ─────────────────────────────────────── */
.stage-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-left: 3px solid #c0c4cc;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

/* 预警状态边框色 */
.stage-card--overdue {
  border-left-color: #f56c6c;
}

.stage-card--delayed {
  border-left-color: #e08e8e;
}

.stage-card--ahead {
  border-left-color: #67c23a;
}

.stage-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 4px;
}

.stage-card__name {
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-card__type {
  flex-shrink: 0;
  font-size: 10px;
  color: #409eff;
  border: 1px solid rgba(64, 158, 255, 0.4);
  border-radius: 3px;
  padding: 0 4px;
  line-height: 16px;
}

.stage-card__type--after {
  color: #e6a23c;
  border-color: rgba(230, 162, 60, 0.4);
}

/* 预警/漂移角标 */
.stage-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}

.warn-badge {
  font-size: 10px;
  line-height: 16px;
  padding: 0 5px;
  border-radius: 3px;
  white-space: nowrap;
}

/* OVERDUE：未完成且已超期（红色） */
.warn-badge--overdue {
  color: #fff;
  background: #f56c6c;
}

/* DELAYED：已完成但延误（灰红） */
.warn-badge--delayed {
  color: #fff;
  background: #e08e8e;
}

/* AHEAD：提前完成（绿色） */
.warn-badge--ahead {
  color: #fff;
  background: #67c23a;
}

/* planDriftDays > 0：计划漂移（橙色角标） */
.warn-badge--drift {
  color: #fff;
  background: #e6a23c;
}

.stage-card__addr {
  color: #606266;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  i {
    margin-right: 2px;
    color: #909399;
  }
}

.stage-card__row {
  margin-bottom: 4px;
}

.stage-card__status {
  font-size: 11px;
  padding: 0 6px;
  line-height: 18px;
  border-radius: 3px;
  display: inline-block;
  color: #909399;
  background: #f0f2f5;
}

.stage-card__status--working {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.12);
}

.stage-card__status--accepted {
  color: #409eff;
  background: rgba(64, 158, 255, 0.12);
}

.stage-card__status--done {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.12);
}

.stage-card__time {
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #606266;
  line-height: 18px;
}

.stage-card__time-label {
  flex-shrink: 0;
  color: #909399;
  font-size: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 2px;
  padding: 0 3px;
  line-height: 14px;
}

/* ── 响应式 ───────────────────────────────────────── */
@media screen and (max-width: 1240px) {
  .week-board__grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media screen and (max-width: 900px) {
  .week-board__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 600px) {
  .week-board__grid {
    grid-template-columns: 1fr;
  }

  .day-col {
    min-height: auto;
  }

  .week-board__header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style lang="scss">
/* 弹窗宽度自适应小屏（append-to-body，需全局样式） */
.week-board-dialog {
  max-width: 96vw;

  .el-dialog__body {
    padding: 14px 20px;
  }
}
</style>
